import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { apiGet } from '../widget-api/http-client';
import { formatAccessTime } from '../widget-lib/access-code-format';
import { saveOfflineCache } from '../widget-lib/offline-cache';
import { initPushRegistration } from '../widget-lib/push-token-register';
import { PATH } from '../widget-lib/path-routes';

type AccessCodeItem = {
  id: string;
  pin?: string;
  pin_prefix?: string;
  qr_data_url: string | null;
  status: string;
  lesson_name: string;
  starts_at: string;
  ends_at: string;
  created_at: string;
};

type AccessCodeListResponse = {
  items: AccessCodeItem[];
  total: number;
};

type TOTPSetupResponse = {
  enabled: boolean;
  otp_secret: string;
  otp_auth_url: string;
  qr_data_url: string;
  issuer: string;
};

type LiveTOTPResponse = {
  pin: string;
  seconds_remaining: number;
  qr_data_url: string;
  interval: number;
};

type LastScanStatusResponse = {
  has_recent_scan: boolean;
  scanned_at?: string;
  success?: boolean;
  fail_reason?: string;
  fail_reason_text?: string;
  lesson_name?: string;
};

function statusLabel(st: string): string {
  switch (st) {
    case 'active': return 'Активен';
    case 'used': return 'Использован';
    case 'expired': return 'Просрочен';
    case 'cancelled': return 'Отменён';
    default: return st;
  }
}

const statusColor: Record<string, string> = {
  active: 'text-green-600 dark:text-green-400',
  used: 'text-gray-500 dark:text-gray-400',
  expired: 'text-yellow-600 dark:text-yellow-400',
  cancelled: 'text-red-600 dark:text-red-400',
};

export default function MyAccessPage() {
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  const suffix = qs ? `?${qs}` : '';
  const { accessToken, bootstrapHint, orgSlug, me } = useEmbedBootstrap();
  const [codes, setCodes] = useState<AccessCodeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totpSetup, setTotpSetup] = useState<TOTPSetupResponse | null>(null);
  const [liveTotp, setLiveTotp] = useState<LiveTOTPResponse | null>(null);
  const [lastScan, setLastScan] = useState<LastScanStatusResponse | null>(null);
  const [countdown, setCountdown] = useState<number>(30);
  const [showFullScreenQR, setShowFullScreenQR] = useState(false);
  const [moduleDisabled, setModuleDisabled] = useState(false);

  const fetchLiveTOTP = useCallback(async () => {
    if (!accessToken) return;
    const q: Record<string, string | number> = {};
    if (orgSlug) q.org_slug = orgSlug;
    if (me?.organization_id) q.org_id = me.organization_id;
    try {
      const data = await apiGet<LiveTOTPResponse>('/api/v1/qrcode/live-totp', q, { accessToken });
      if (data?.pin) {
        setLiveTotp(data);
        setCountdown(data.seconds_remaining ?? 30);
      }
    } catch {
      // ignore
    }
  }, [accessToken, orgSlug, me?.organization_id]);

  const fetchLastScanStatus = useCallback(async () => {
    if (!accessToken) return;
    const q: Record<string, string | number> = {};
    if (orgSlug) q.org_slug = orgSlug;
    if (me?.organization_id) q.org_id = me.organization_id;
    try {
      const res = await apiGet<LastScanStatusResponse>('/api/v1/qrcode/last-scan-status', q, { accessToken });
      if (res?.has_recent_scan) {
        setLastScan(res);
      }
    } catch {
      // ignore
    }
  }, [accessToken, orgSlug, me?.organization_id]);

  useEffect(() => {
    if (!accessToken) return;
    fetchLiveTOTP();
    fetchLastScanStatus();
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          fetchLiveTOTP();
          return 30;
        }
        return prev - 1;
      });
    }, 1000);

    const scanPoller = setInterval(() => {
      fetchLastScanStatus();
    }, 3000);

    return () => {
      clearInterval(timer);
      clearInterval(scanPoller);
    };
  }, [accessToken, fetchLiveTOTP, fetchLastScanStatus]);

  const fetch = useCallback(async (isBackground = false) => {
    if (!accessToken) return;
    if (!isBackground) {
      setLoading(true);
    }
    setModuleDisabled(false);
    const q: Record<string, string | number> = {};
    if (orgSlug) q.org_slug = orgSlug;
    if (me?.organization_id) q.org_id = me.organization_id;
    try {
      const res = await apiGet<AccessCodeItem[] | AccessCodeListResponse>('/api/v1/qrcode/codes/active', { limit: '20', ...q }, { accessToken });
      const items = Array.isArray(res) ? res : (res?.items || []);
      setCodes(items);
      setTotal(items.length);
    } catch {
      setCodes([]);
      setTotal(0);
    }

    try {
      const setup = await apiGet<TOTPSetupResponse>('/api/v1/qrcode/totp-setup', q, { accessToken });
      setTotpSetup(setup);
    } catch (e: unknown) {
      const err = e as { status?: number; code?: string } | undefined;
      if (err?.status === 403 || err?.code === 'FORBIDDEN' || err?.code === 'module_disabled') {
        setModuleDisabled(true);
      }
    } finally {
      setLoading(false);
    }
  }, [accessToken, orgSlug, me?.organization_id]);

  useEffect(() => {
    fetch(false);
    const interval = setInterval(() => {
      void fetch(true);
    }, 30000);
    return () => clearInterval(interval);
  }, [fetch]);

  // Офлайн-кэш для мобильного стартового экрана (Capacitor Preferences).
  useEffect(() => {
    if (!accessToken || !me) return;
    const secret = totpSetup?.otp_secret;
    if (!secret) return;
    const interval = liveTotp?.interval ?? 30;
    void saveOfflineCache({
      org_slug: orgSlug ?? undefined,
      org_id: me.organization_id ?? undefined,
      user_id: me.id,
      client_name: me.full_name ?? undefined,
      client_secret: secret,
      otp_interval: interval,
      bookings: codes.map((c) => ({
        lesson_name: c.lesson_name,
        starts_at: c.starts_at,
        ends_at: c.ends_at,
        pin_prefix: c.pin_prefix,
        qr_data_url: c.qr_data_url ?? undefined,
      })),
      cached_at: new Date().toISOString(),
    });
  }, [accessToken, me, orgSlug, codes, totpSetup, liveTotp]);

  // Регистрация push-токена устройства (best-effort; listener + tap + register).
  useEffect(() => {
    if (!accessToken) return;
    void initPushRegistration();
  }, [accessToken]);

  if (bootstrapHint && !orgSlug) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Мои коды доступа</h1>
        <p className="text-sm text-[var(--text-muted)]">{bootstrapHint}</p>
      </div>
    );
  }

  if (!accessToken) {
    return (
      <div className="space-y-4 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Мои коды доступа</h1>
        <p className="text-sm text-[var(--text-muted)]">Войдите, чтобы увидеть коды доступа.</p>
        <div className="flex flex-wrap gap-2">
          <Link to={`${PATH.signIn}${suffix}`}
            className="inline-flex h-9 items-center justify-center rounded-sm bg-[var(--btn-primary-bg)] px-3.5 text-[13px] font-medium text-[var(--btn-primary-fg)] hover:bg-[var(--btn-primary-bg-hover)]">
            Войти
          </Link>
        </div>
      </div>
    );
  }

  if (moduleDisabled) {
    return (
      <div className="space-y-4 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Мои коды доступа</h1>
        <div className="rounded-xl border border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800 p-4 text-sm text-yellow-800 dark:text-yellow-200">
          <p className="font-medium">Модуль QR-доступа не активирован</p>
          <p className="mt-1 text-xs text-yellow-700 dark:text-yellow-300">
            Для данной организации проход по QR-коду не включен в тарифное соглашение.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full min-w-0">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Мои коды доступа</h1>
          <p className="mt-1 text-sm text-[var(--text-muted)]">
            {me ? (me.full_name || 'Пользователь') : ''}
          </p>
        </div>
      </div>

      {/* ── Real-time Scan Status Banner (Short Polling) ── */}
      {lastScan && lastScan.has_recent_scan && (
        <div className={`rounded-xl border p-4 text-sm flex items-start justify-between gap-3 shadow-md animate-fade-in ${
          lastScan.success
            ? 'bg-emerald-50 border-emerald-300 text-emerald-900 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-200'
            : 'bg-amber-50 border-amber-300 text-amber-900 dark:bg-amber-950/40 dark:border-amber-800 dark:text-amber-200'
        }`}>
          <div className="space-y-1">
            <div className="flex items-center gap-2 font-semibold">
              <span>{lastScan.success ? '✅ Успешный проход на турникете' : '⛔ Доступ отклонен турникетом'}</span>
            </div>
            <p className="text-xs">
              {lastScan.success
                ? `Дверь открыта. Наслаждайтесь занятием!`
                : `Причина: ${lastScan.fail_reason_text || lastScan.fail_reason || 'Неизвестная ошибка'}`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setLastScan(null)}
            className="text-xs opacity-70 hover:opacity-100 font-medium px-2 py-0.5 rounded"
          >
            ✕
          </button>
        </div>
      )}

      {/* ── Dynamic 30-Second TOTP Live QR Widget ── */}
      {liveTotp && (
        <div className="rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-5 shadow-[var(--shadow-card)] space-y-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="relative flex h-3 w-3 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
              </span>
              <span className="font-semibold text-sm text-[var(--text)] truncate">Динамический QR-код</span>
            </div>
            <span className="shrink-0 whitespace-nowrap tabular-nums rounded-full bg-[var(--bg-soft)] px-2.5 py-1 text-[11px] font-mono font-medium text-[var(--text-muted)] border border-[var(--border)]">
              ⏱ {countdown} сек
            </span>
          </div>

          <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 rounded-full overflow-hidden">
            <div
              className="bg-emerald-500 h-full transition-all duration-1000 ease-linear"
              style={{ width: `${(countdown / (liveTotp.interval || 30)) * 100}%` }}
            />
          </div>

          <div className="flex flex-col items-center justify-center space-y-3 py-2">
            <button
              type="button"
              onClick={() => setShowFullScreenQR(true)}
              className="relative group cursor-pointer focus:outline-none"
              title="Нажмите, чтобы развернуть QR-код на весь экран"
            >
              <img
                src={liveTotp.qr_data_url}
                alt="Динамический QR-код"
                className="w-52 h-52 rounded-xl border border-[var(--border)] bg-white p-3 shadow-sm transition-all duration-300 group-hover:scale-105 group-hover:shadow-md"
              />
              <span className="absolute bottom-2 right-2 rounded-md bg-black/75 px-1.5 py-0.5 text-[10px] font-medium text-white shadow backdrop-blur-sm group-hover:bg-black">
                ⛶ На весь экран
              </span>
            </button>

            <button
              type="button"
              onClick={() => setShowFullScreenQR(true)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--accent)] hover:underline"
            >
              ⛶ Раскрыть на весь экран
            </button>

            <div className="flex flex-col items-center space-y-1 pt-1">
              <span className="text-xs text-[var(--text-muted)]">Одноразовый PIN-код турникета:</span>
              <div className="font-mono text-2xl font-bold tracking-widest text-[var(--text)] bg-[var(--bg-soft)] px-4 py-1.5 rounded-lg border border-[var(--border)]">
                {liveTotp.pin.slice(0, 3)} {liveTotp.pin.slice(3)}
              </div>
            </div>
          </div>

          <p className="text-[11px] text-[var(--text-muted)] text-center">
            Код и QR-код автоматически обновляются каждые 30 секунд. Сканируйте сканером турникета или вводите цифры на PIN-паде.
          </p>
        </div>
      )}

      {/* ── Fullscreen QR Code Modal ── */}
      {showFullScreenQR && liveTotp && (
        <div
          className="fixed inset-0 z-50 flex flex-col items-center justify-between bg-black/95 p-4 sm:p-6 backdrop-blur-md"
          onClick={() => setShowFullScreenQR(false)}
        >
          {/* Top Bar */}
          <div className="w-full flex items-center justify-between text-white max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
              </span>
              <span className="text-sm font-medium">Код доступа</span>
            </div>
            <button
              type="button"
              onClick={() => setShowFullScreenQR(false)}
              className="rounded-full bg-white/20 p-2 text-white hover:bg-white/30 transition-colors"
              aria-label="Закрыть"
            >
              ✕
            </button>
          </div>

          {/* Center Card (High Contrast White Card for Optical Scanner) */}
          <div
            className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl flex flex-col items-center space-y-4 text-center my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full flex items-center justify-between gap-2 text-xs text-gray-500 font-medium">
              <span className="min-w-0 truncate">{me?.full_name || 'Пропуск'}</span>
              <span className="shrink-0 whitespace-nowrap tabular-nums font-mono bg-gray-100 px-2 py-0.5 rounded-full">⏱ {countdown} сек</span>
            </div>

            {/* Timer bar */}
            <div className="w-full bg-gray-200 h-1.5 rounded-full overflow-hidden">
              <div
                className="bg-emerald-500 h-full transition-all duration-1000 ease-linear"
                style={{ width: `${(countdown / (liveTotp.interval || 30)) * 100}%` }}
              />
            </div>

            {/* Big QR Code with Quiet Zone Padding */}
            <div className="p-2 bg-white rounded-2xl border border-gray-100 shadow-inner">
              <img
                src={liveTotp.qr_data_url}
                alt="QR-код доступа"
                className="w-64 h-64 sm:w-72 sm:h-72 object-contain"
              />
            </div>

            {/* Big High-Contrast PIN */}
            <div className="w-full space-y-1">
              <span className="text-xs text-gray-500 font-medium">PIN для турникета:</span>
              <div className="font-mono text-3xl font-extrabold tracking-widest text-gray-900 bg-gray-100 py-2 px-4 rounded-xl border border-gray-200">
                {liveTotp.pin.slice(0, 3)} {liveTotp.pin.slice(3)}
              </div>
            </div>

            <p className="text-xs text-gray-500">
              Поднесите экран к сканеру или введите 6 цифр на PIN-паде
            </p>
          </div>

          {/* Bottom Close Button */}
          <div className="w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setShowFullScreenQR(false)}
              className="w-full h-12 rounded-xl bg-white/20 text-white font-medium text-sm hover:bg-white/30 transition-colors flex items-center justify-center gap-2"
            >
              Закрыть
            </button>
          </div>
        </div>
      )}

      {/* Active Bookings List */}
      {loading ? (
        <p className="text-sm text-[var(--text-muted)] text-center py-4">Загрузка ваших записей...</p>
      ) : codes.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-6 space-y-3">
          <p className="text-sm text-[var(--text-muted)]">Активных записей пока нет.</p>
          <Link to={`${PATH.schedule}${suffix}`}
            className="inline-flex h-9 items-center justify-center rounded-sm bg-[var(--btn-primary-bg)] px-3.5 text-[13px] font-medium text-[var(--btn-primary-fg)] hover:bg-[var(--btn-primary-bg-hover)]">
            Записаться на занятие
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[var(--text)]">Активные записи для прохода ({total})</h2>
            <span className="text-xs text-[var(--text-muted)]">Доступ по единому QR-коду</span>
          </div>
          <ul className="space-y-2.5">
            {codes.map((c) => (
              <li
                key={c.id}
                className="rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] p-3.5 text-sm shadow-sm flex items-center justify-between gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[var(--text)] truncate">{c.lesson_name}</span>
                    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-md bg-[var(--bg-soft)] ${statusColor[c.status] || ''}`}>
                      {statusLabel(c.status)}
                    </span>
                  </div>
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    {formatAccessTime(c.starts_at, c.ends_at)}
                  </p>
                </div>
                <div className="flex-shrink-0 text-right">
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2.5 py-1 rounded-lg border border-emerald-200 dark:border-emerald-800">
                    ✓ Доступ активен
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
